import type { CSSObject } from '@mui/material/styles';
import { styled } from '@mui/material/styles';

import { colorTokens } from '../../tokens/colors.ts';

/** Outer radio circle size. */
const RADIO_SIZE = 16;
/** Unchecked border width. */
const UNCHECKED_BORDER_WIDTH = 1;
/** Checked ring width. */
const CHECKED_RING_WIDTH = 4;
/** Pressed unchecked border width. */
const PRESSED_UNCHECKED_BORDER_WIDTH = 2;
/** Pressed checked ring width. */
const PRESSED_CHECKED_RING_WIDTH = 5;
/** Focus halo width outside the radio. */
const FOCUS_HALO_WIDTH = 3;

const { semantic, global } = colorTokens;

/** Combines the radio fill stroke with the outer focus halo from Figma. */
const withFocusHalo = (innerShadow: string): string =>
  `${innerShadow}, 0 0 0 ${FOCUS_HALO_WIDTH}px ${global.primary[200]}`;

/** Default (unchecked) radio icon. */
export const KestrixRadioItemDefault = styled('span')(() => ({
  borderRadius: '50%',
  width: RADIO_SIZE,
  height: RADIO_SIZE,
  flexShrink: 0,
  display: 'inline-block',
  boxSizing: 'border-box',
  backgroundColor: semantic.brand.base.white,
  boxShadow: `inset 0 0 0 ${UNCHECKED_BORDER_WIDTH}px ${global.neutral[400]}`,
  transition: 'box-shadow 150ms ease',
  'input:hover ~ &': {
    boxShadow: `inset 0 0 0 ${UNCHECKED_BORDER_WIDTH}px ${global.neutral[700]}`,
  },
  'input:active ~ &': {
    boxShadow: `inset 0 0 0 ${PRESSED_UNCHECKED_BORDER_WIDTH}px ${global.neutral[800]}`,
  },
  'input:disabled ~ &': {
    backgroundColor: semantic.brand.base.white,
    boxShadow: `inset 0 0 0 ${UNCHECKED_BORDER_WIDTH}px ${global.neutral[200]}`,
  },
  '.Mui-focusVisible &': {
    boxShadow: withFocusHalo(
      `inset 0 0 0 ${UNCHECKED_BORDER_WIDTH}px ${global.neutral[400]}`,
    ),
  },
}));

const checkedRingShadow = (width: number, color: string): string =>
  `inset 0 0 0 ${width}px ${color}`;

/** Checked radio icon — thick purple ring with white centre. */
export const KestrixRadioItemChecked = styled(KestrixRadioItemDefault)({
  backgroundColor: semantic.brand.base.white,
  boxShadow: checkedRingShadow(CHECKED_RING_WIDTH, semantic.brand.primary),
  'input:hover ~ &': {
    boxShadow: checkedRingShadow(CHECKED_RING_WIDTH, global.primary[500]),
  },
  'input:active ~ &': {
    boxShadow: checkedRingShadow(
      PRESSED_CHECKED_RING_WIDTH,
      global.primary[600],
    ),
  },
  'input:disabled ~ &': {
    backgroundColor: semantic.brand.base.white,
    boxShadow: checkedRingShadow(CHECKED_RING_WIDTH, global.primary[200]),
  },
  '.Mui-focusVisible &': {
    boxShadow: withFocusHalo(
      checkedRingShadow(CHECKED_RING_WIDTH, semantic.brand.primary),
    ),
  },
} as CSSObject);
